/**
 * CD (Consumer Durable) Insert Service
 * Migrated from 3in1cms legacy/cdInsertApi.ts
 */

import { request } from "../core/request";
import { EVENT_CODE, OPERATION_IDS } from "./constants";
// import { dataLayer } from "@repo/analytics/data-layer-utils";
import { generateCTLogData } from "@repo/shared-utils/easyEMIJourney";
import type { ICdInsertData } from "../types/easyEMIJourneyTypes";

interface IUTMParamsEasyEmi {
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_source: string | null;
}

interface ICdInsert {
  id: string;
  description: string;
  statusCode: string;
  txId: string;
  dbResponse: string;
  eventCode: string;
}

const ALLOWED_UTM_SOURCES = [
  "loanpod2_ARM1",
  "loanpod2_ARM2",
  "loanpod2_ARM3",
  "loanpod2_ARM4",
  "loanpod2_ARM5",
  "loanpod2_ARM6",
  "loanpod2_sms_prospect",
  "loanpod2_sms_ntb",
  "loanpod2_sms_ptb",
  "loanpod2_Intellectads",
  "PayTeamServiceComm",
  "loanpod2_InstaRetargeting",
  "STR",
  "EZD",
  "loanpod2_Email3",
  "loanpod2_Email4",
  "affiliatepartnername_mktg",
  "paisabazaar_mktg",
  "phonepe_mktg",
  "mymoneymantra_mktg",
];

const DEFAULT_UTM_VALUE = {
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
};

/**
 * Get URL parameter value
 * @param param - Parameter name
 * @returns Parameter value or null
 */
const getParams = (param: string): string | null => {
  if (typeof window === "undefined") return null;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(param);
};

/**
 * Get value or return null if undefined/empty
 * @param value - Value to check
 * @returns Value or null
 */
const getOrDefaultValue = (value: string | undefined): string | null => {
  return value && value.trim() !== "" ? value : null;
};

/**
 * Filter UTM parameters based on allowed sources
 * @param utmParams - UTM parameters
 * @returns Filtered UTM parameters
 */
const filterUtmParams = (
  utmParams: IUTMParamsEasyEmi | null
): {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
} => {
  try {
    if (!utmParams) {
      return DEFAULT_UTM_VALUE;
    }

    const utmSource = utmParams?.utm_source?.trim() || "";

    // Edge case: Handle empty or invalid UTM source
    if (!utmSource) {
      return DEFAULT_UTM_VALUE;
    }

    // Check if UTM source is in allowed list
    const isAllowedSource = ALLOWED_UTM_SOURCES.includes(utmSource);

    if (isAllowedSource) {
      // Return UTM values if source is allowed
      return {
        utm_source: getOrDefaultValue(utmParams.utm_source ?? undefined),
        utm_medium: getOrDefaultValue(utmParams.utm_medium ?? undefined),
        utm_campaign: getOrDefaultValue(utmParams.utm_campaign ?? undefined),
      };
    }

    // Return null values if source is not allowed
    return DEFAULT_UTM_VALUE;
  } catch (error) {
    console.error("Error filtering UTM parameters:", error);
    return DEFAULT_UTM_VALUE;
  }
};

/**
 * Insert CD (Consumer Durable) data
 * @param data - CD insert data
 * @returns CD insert response or null on error
 */
export const cdInsert = async (data: ICdInsertData): Promise<ICdInsert | null> => {
  try {
    const utm: IUTMParamsEasyEmi = {
      utm_medium: getParams("utm_medium"),
      utm_campaign: getParams("utm_campaign"),
      utm_source: getParams("utm_source"),
    };

    const filteredUtm = filterUtmParams(utm);

    const reqData = {
      ...data,
      eventCode: data?.eventCode || EVENT_CODE.CD_LOAN,
      assetCategory: data?.assetCategory || null,
      sfdcCategory: data?.sfdcCategory || null,
      customerType: data?.customerId ? "NTB" : null, // Simplified from component dependencies
      customerId: null,
      utm_source: filteredUtm.utm_source,
      utm_campaign: filteredUtm.utm_campaign,
      utm_medium: filteredUtm.utm_medium,
    };

    // dataLayer("", generateCTLogData("cdInsert", "cdInsertAPI Initiated"));

    const response = (await request<ICdInsert, typeof reqData>({
      operationId: OPERATION_IDS.cdInsert,
      requestData: { ...reqData },
    })) as ICdInsert;

    // dataLayer("", generateCTLogData("cdInsert", "cdInsertAPI Success"));
    return response;
  } catch (error) {
    // dataLayer("", generateCTLogData("cdInsert", "Error fetching cd insert data"));
    return null;
  } finally {
    // dataLayer("", generateCTLogData("cdInsert", "cdInsertAPI Finally Executed"));
  }
};
